import { OrderStatus, type OrderType } from "@prisma/client";

import { isDirectShip } from "~/shared/orders/order-type";

type InvoiceReadyOrder = {
  orderNo: string;
  status: OrderStatus;
  type: OrderType;
  shippedAt: Date | null;
  items: Array<{
    productId: string | null;
    lotId: string | null;
    lotConsumption?: { id: string } | null;
    /** Server-certified current physical membership; never a client-authored lot substitute. */
    inventorySourceReady?: boolean;
  }>;
};

const statusBlockers = new Map<OrderStatus, (orderNo: string) => string>([
  [OrderStatus.DRAFT, orderNo => `Draft Sales Order ${orderNo} must be confirmed before invoicing.`],
  [OrderStatus.VOID, orderNo => `Voided Sales Order ${orderNo} cannot be invoiced.`],
  [OrderStatus.COMPLETED, orderNo => `Completed Sales Order ${orderNo} cannot be invoiced.`],
]);

/** Coarse discovery optimization only; this owner still checks all readiness conditions below. */
export const ORDER_STATUSES_BLOCKING_INVOICING: readonly OrderStatus[] = [...statusBlockers.keys()];

/** Shared by the single-order action and multi-order draft/post checks. */
export function orderInvoiceReadinessBlocker(order: InvoiceReadyOrder): string | null {
  const statusBlocker = statusBlockers.get(order.status);
  if (statusBlocker) {
    return statusBlocker(order.orderNo);
  }
  const productItems = order.items.filter(item => item.productId !== null);
  if (
    isDirectShip(order.type) &&
    order.shippedAt === null &&
    !(productItems.length > 0 && productItems.every(item => item.inventorySourceReady === true))
  ) {
    return `Direct Ship Sales Order ${order.orderNo} must be shipped before invoicing.`;
  }
  const hasUnreadyProduct = order.items.some(
    item =>
      item.productId !== null &&
      item.inventorySourceReady !== true &&
      item.lotId === null &&
      (!isDirectShip(order.type) || item.lotConsumption == null),
  );
  if (hasUnreadyProduct) {
    return `Sales Order ${order.orderNo} needs lot readiness for every product item before invoicing.`;
  }
  return null;
}
